/**
 * The one request helper every page uses (www-site.md §1.4), plus the stored session token
 * (§1.3).
 */

export const TOKEN_KEY = 'nodenet_token';

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string) {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    // storage blocked: the session lasts for this page only
  }
}

export function clearToken() {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    // ignore
  }
}

/** Fired when an authed call answers 401 (the token has already been removed). */
export const UNAUTHORIZED_EVENT = 'nodenet:unauthorized';

export const SESSION_EXPIRED = 'Your session has expired. Please sign in again.';

export type Body =
  { form: Record<string, string | number | boolean> } | { json: unknown } | { multipart: FormData };

export interface CallOptions {
  method?: string;
  body?: Body;
  /** send the stored session token (`true`), a given token, or none (default) */
  auth?: boolean | string;
  /** shown for a 400 with an empty body (several `accounts` routes refuse that way) */
  emptyBadRequest?: string;
  signal?: AbortSignal;
}

export class ApiError extends Error {
  readonly status: number;
  readonly body: Record<string, unknown>;
  /** the request never got an answer */
  readonly network: boolean;
  constructor(message: string, status: number, body: Record<string, unknown>, network = false) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    this.network = network;
  }
}

const OAUTH_CODE = /^[a-z]+(?:_[a-z]+)*$/;

/** The sentence to show for a refused call (§1.4). */
export function errorMessage(
  status: number,
  body: Record<string, unknown>,
  opts: { authed: boolean; emptyBody: boolean; emptyBadRequest?: string },
): string {
  if (status === 401 && opts.authed) return SESSION_EXPIRED;
  if (status === 400 && opts.emptyBody && opts.emptyBadRequest) return opts.emptyBadRequest;
  const error = typeof body.error === 'string' && body.error !== '' ? body.error : null;
  const description =
    typeof body.error_description === 'string' && body.error_description !== ''
      ? body.error_description
      : null;
  if (error && !(OAUTH_CODE.test(error) && description)) return error;
  if (description) return description;
  if (error) return error;
  return `Request failed (${status}).`;
}

function parseBody(text: string): Record<string, unknown> | unknown[] {
  if (!text.trim()) return {};
  try {
    const v: unknown = JSON.parse(text);
    if (v !== null && typeof v === 'object') return v as Record<string, unknown>;
    return {};
  } catch {
    return {};
  }
}

export async function call<T = Record<string, unknown>>(
  url: string,
  opts: CallOptions = {},
): Promise<T> {
  const headers: Record<string, string> = {};
  let token: string | null = null;
  if (opts.auth === true) token = getToken();
  else if (typeof opts.auth === 'string') token = opts.auth;
  const authed = opts.auth !== undefined && opts.auth !== false;
  if (token) headers.Authorization = `Bearer ${token}`;

  let payload: BodyInit | undefined;
  const b = opts.body;
  if (b && 'form' in b) {
    headers['Content-Type'] = 'application/x-www-form-urlencoded';
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(b.form)) p.set(k, String(v));
    payload = p.toString();
  } else if (b && 'json' in b) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify(b.json);
  } else if (b && 'multipart' in b) {
    payload = b.multipart; // the browser writes the boundary
  }

  let res: Response;
  try {
    res = await fetch(url, {
      method: opts.method ?? (payload === undefined ? 'GET' : 'POST'),
      headers,
      body: payload,
      signal: opts.signal,
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    throw new ApiError(
      "Couldn't reach the server. Check your connection and try again.",
      0,
      {},
      true,
    );
  }

  let text: string;
  try {
    text = await res.text();
  } catch {
    text = '';
  }
  const parsed = parseBody(text);

  if (!res.ok) {
    const body = Array.isArray(parsed) ? {} : parsed;
    if (res.status === 401 && authed) {
      clearToken();
      window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
    }
    throw new ApiError(
      errorMessage(res.status, body, {
        authed,
        emptyBody: text.trim() === '',
        emptyBadRequest: opts.emptyBadRequest,
      }),
      res.status,
      body,
    );
  }
  return parsed as T;
}

export function messageOf(e: unknown): string {
  if (e instanceof Error) return e.message;
  return String(e);
}

/**
 * A multipart POST with upload progress (fetch can't report it, so this uses XHR). Same auth,
 * error sentences and 401 handling as `call`; aborting `signal` cancels the upload with an
 * `AbortError`.
 */
export function upload<T = Record<string, unknown>>(
  url: string,
  form: FormData,
  opts: { onProgress?: (loaded: number, total: number) => void; signal?: AbortSignal } = {},
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    const token = getToken();
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) opts.onProgress?.(e.loaded, e.total);
    };
    const abort = () => xhr.abort();
    opts.signal?.addEventListener('abort', abort, { once: true });
    xhr.onabort = () => reject(new DOMException('The upload was canceled.', 'AbortError'));
    xhr.onerror = () =>
      reject(
        new ApiError(
          "Couldn't reach the server. Check your connection and try again.",
          0,
          {},
          true,
        ),
      );
    xhr.onload = () => {
      opts.signal?.removeEventListener('abort', abort);
      const text = xhr.responseText ?? '';
      const parsed = parseBody(text);
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(parsed as T);
        return;
      }
      const body = Array.isArray(parsed) ? {} : parsed;
      if (xhr.status === 401) {
        clearToken();
        window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
      }
      reject(
        new ApiError(
          errorMessage(xhr.status, body, { authed: true, emptyBody: text.trim() === '' }),
          xhr.status,
          body,
        ),
      );
    };
    if (opts.signal?.aborted) {
      reject(new DOMException('The upload was canceled.', 'AbortError'));
      return;
    }
    xhr.send(form);
  });
}
